import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { FC, MouseEventHandler } from "react";
import { Link } from "remix";
import { Id } from "~/new_shared/db/db.server";
import { FullUser } from "~/new_shared/db/user.server";
import { teamEmailAddress, TntAllUrls, TntUser, Token } from "~/new_shared/urls";

/* 

Best practices for components:
* they should never use padding or otherwise create space outside of the borders of the control
   - this is because different uses of a component may require different padding/etc, 
   - instead of using padding on component, we should use layout around the component to create that space

*/

export interface WithUser {
  user: FullUser;
}

export const LogoutButton = () => (
  <form action={TntAllUrls.Logout()} method="POST">
    <Button>Sign out</Button>
  </form>
);

interface ButtonProps {
  onClick?: MouseEventHandler;
  name?: string;
  value?: string;
  className?: string;
  disabled?: boolean;
  title?: string;
}
export const ButtonClasses = `inline-flex items-center px-4 py-1.5 border border-transparent
text-base font-medium rounded-md shadow-sm text-white bg-amber-400 hover:bg-amber-500
focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 no-underline disabled:bg-slate-300`;

export const Button: FC<ButtonProps> = ({
  children,
  onClick,
  name,
  value,
  className,
  disabled,
  title,
}) => (
  <button
    type="submit"
    className={classNames(ButtonClasses, className ?? "")}
    onClick={onClick}
    name={name}
    value={value}
    disabled={disabled}
    title={title}
  >
    {children}
  </button>
);

export const PageHeader: FC = ({ children }) => (
  <header>
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold leading-tight text-gray-900">{children}</h1>
    </div>
  </header>
);

export const NarrowPageContents: FC = ({ children }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    {/* We've used 3xl here, but feel free to try other max-widths based on your needs */}
    <div className="max-w-xl mx-auto">{children}</div>
  </div>
);

export const ModalBoxOutline: FC = ({ children }) => (
  <div className="bg-white shadow sm:rounded-lg">
    <div className="px-4 py-5 sm:p-6">{children}</div>
  </div>
);

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const EmailContents = ({
  emailId,
  userToken,
  devBuild,
  showImg,
}: {
  emailId: Id;
  userToken: Token;
  devBuild: boolean;
  showImg: boolean;
}) => (
  <>
    {/* WARNING: this frame will display user generated content
      IT MUST BE SANDBOXED as part of several layers of security 
      `sandbox=""` means use all the sandboxing restrictions available
    */}
    <iframe
      className="w-full h-full"
      src={TntUser.IndividualEmailHtmlUNSAFE(
        devBuild ? "local" : "prod",
        emailId,
        userToken,
        showImg,
      )}
      sandbox="allow-top-navigation-by-user-activation allow-popups"
      placeholder="Loading..."
    />
  </>
);

export const QuestionsContactUsBlurb = () => (
  <div>
    Still have questions? Contact us at{" "}
    <a href={"mailto:" + teamEmailAddress}>{teamEmailAddress}</a> - we reply to every email!
  </div>
);

export const CTALinkButton: FC<{ url: string }> = ({ url, children }) => (
  <Link to={url} className={classNames(ButtonClasses, "mx-auto mb-16")}>
    {children}
  </Link>
);

export const TryForFreeBlurb = ({ user }: { user: FullUser | undefined }) => {
  const isUser = !!user;
  return (
    <div className={classNames("text-center ", isUser ? "hidden" : "block")}>
      <CTALinkButton url={TntAllUrls.SignUp()}>Try ThxNoThx for Free</CTALinkButton>
    </div>
  );
};

export const HelpIcon: FC<{ link: string }> = ({ link }) => {
  return (
    <Link to={link} className="text-slate-500">
      <QuestionMarkCircleIcon className="w-4 h-4 inline mx-1" />
    </Link>
  );
};
